import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_PriceTypeConfig,
  selectPriceTypeConfig,
} from "../../../../redux/IchthusSlice";
import {
  X,
  RotateCcw,
  Search,
  Trash2,
  User,
  MapPin,
  CheckCircle,
  ArrowRight,
  Package,
  Loader2,
  Plus,
  Check,
  ChevronRight,
  ChevronDown,
  ScanBarcode,
  Settings2,
  Truck,
} from "lucide-react";
import { domain } from "../../../../security";
import SelectedSerialModal from "../../POS/PosModule/SelectedSerialModal";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const inputCls =
  "block w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none transition focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400";
const labelCls =
  "block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide";

// ─── PRICE TYPE DEFINITIONS ─────────────────────────
const ALL_PRICE_TYPES = [
  {
    configKey: "showVatInc",
    field: "vatInc",
    label: "VAT Inc",
    fullLabel: "VAT Inclusive",
  },
  {
    configKey: "showVatEx",
    field: "vatEx",
    label: "VAT Ex",
    fullLabel: "VAT Exclusive",
  },
  {
    configKey: "showReseller",
    field: "reseller",
    label: "Reseller",
    fullLabel: "Reseller Price",
  },
  {
    configKey: "showZeroRated",
    field: "zeroRated",
    label: "Zero Rated",
    fullLabel: "Zero Rated",
  },
];

const StepIndicator = ({ step }) => {
  const steps = [
    { n: 1, label: "Customer & Items" },
    { n: 2, label: "Review" },
  ];
  return (
    <div className="flex items-center">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step === s.n
                  ? "bg-emerald-600 text-white shadow-sm shadow-emerald-200"
                  : step > s.n
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
            >
              {step > s.n ? <CheckCircle size={14} /> : s.n}
            </div>
            <span
              className={`text-xs font-semibold hidden sm:block ${
                step === s.n
                  ? "text-emerald-600"
                  : step > s.n
                    ? "text-emerald-600"
                    : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-3 ${
                step > s.n ? "bg-emerald-300" : "bg-slate-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── CUSTOMER MODAL ─────────────────────────
const CustomerSelectionModal = ({ isOpen, onClose, customers, onSelect }) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    return customers.filter((c) =>
      (c.customerName || "").toLowerCase().includes(search.toLowerCase()),
    );
  }, [customers, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex h-full max-h-[75vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 bg-slate-50">
          <div className="flex items-center gap-2 text-slate-800 font-bold">
            <User className="text-emerald-500" size={18} />
            <h2>Select Customer</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              autoFocus
              placeholder="Search customer name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={inputCls + " pl-9"}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {search.trim() && (
            <button
              onClick={() =>
                onSelect({ id: null, customerName: search.trim() })
              }
              className="w-full text-left px-4 py-3 mb-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors flex items-center gap-3"
            >
              <div className="p-1.5 bg-emerald-500 text-white rounded-md shrink-0">
                <Plus size={14} />
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">
                  Use "{search.trim()}"
                </p>
                <p className="text-[10px] text-emerald-600 font-medium">
                  Add as a one-time custom name
                </p>
              </div>
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <p className="text-sm font-semibold">No customers found.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((c) => {
                const pType = c.priceType || c.PriceType;

                return (
                  <button
                    key={c.id}
                    onClick={() => onSelect(c)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-xl transition-colors flex items-center justify-between group"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-slate-800">
                          {c.customerName}
                        </p>
                        {pType && (
                          <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                            {pType}
                          </span>
                        )}
                      </div>
                      {c.address && (
                        <p className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[300px]">
                          {c.address}
                        </p>
                      )}
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-slate-300 group-hover:text-emerald-500 transition-colors"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PRODUCT MODAL ─────────────────────────
const ProductSelectionModal = ({
  isOpen,
  onClose,
  products,
  items,
  onAdd,
  priceType,
}) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    let list = products;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = products.filter(
        (p) =>
          (p.productName && p.productName.toLowerCase().includes(q)) ||
          (p.itemCode && p.itemCode.toLowerCase().includes(q)) ||
          (p.barCode && p.barCode.toLowerCase().includes(q)),
      );
    }
    const unique = [];
    const seen = new Set();
    for (const m of list) {
      if (!seen.has(m.productId)) {
        seen.add(m.productId);
        unique.push(m);
      }
    }
    return unique.slice(0, 50);
  }, [products, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex h-full max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 bg-slate-50">
          <div className="flex items-center gap-2 text-slate-800 font-bold">
            <Package className="text-emerald-500" size={18} />
            <h2>Browse & Add Products</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              autoFocus
              placeholder="Search by product name, barcode, or item code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={inputCls + " pl-9 bg-slate-50 focus:bg-white"}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Package size={32} className="mx-auto mb-3 text-slate-200" />
              <p className="text-sm font-semibold">No products found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.map((p) => {
                const isAdded = items.some((i) => i.productId === p.productId);
                return (
                  <div
                    key={p.productId}
                    className={`flex items-center justify-between p-3 border rounded-xl transition-all ${isAdded ? "bg-slate-50 border-slate-200 opacity-60" : "bg-white border-slate-200 shadow-sm hover:border-emerald-300 hover:shadow-md"}`}
                  >
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-bold text-slate-800 truncate">
                          {p.productName}
                        </p>
                        {p.hasSerial && (
                          <span className="bg-slate-200 text-slate-600 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                            Serials
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          {p.itemCode || "N/A"}
                        </span>
                        {p.brandName && 
                          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                            {p.brandName || ""}
                          </span>
                        }
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                          {formatCurrency(p[priceType] || 0)}
                        </span>
                      </div>
                    </div>
                    <button
                      disabled={isAdded}
                      onClick={() => onAdd(p)}
                      className={`shrink-0 flex items-center justify-center h-8 w-8 rounded-lg transition-colors ${isAdded ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white"}`}
                    >
                      {isAdded ? <Check size={14} /> : <Plus size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 p-4 bg-white flex justify-between items-center">
          <p className="text-sm font-bold text-slate-600">
            Selected: <span className="text-emerald-600">{items.length}</span>
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 text-white text-sm font-bold rounded-xl hover:bg-slate-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────
export default function AddTradeReturnModal({
  onClose,
  onSuccess,
  currentUserId,
}) {
  const dispatch = useDispatch();
  const priceTypeConfig = useSelector(selectPriceTypeConfig);

  const [step, setStep] = useState(1);
  const [isFetchingBase, setIsFetchingBase] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  // Serials State
  const [isSerialModalOpen, setIsSerialModalOpen] = useState(false);
  const [currentSerialProduct, setCurrentSerialProduct] = useState(null);
  const [returnedSerials, setReturnedSerials] = useState({});

  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pricelists, setPricelists] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [priceType, setPriceType] = useState("");
  const [reason, setReason] = useState("");

  // Logistics State
  const [logisticsType, setLogisticsType] = useState("Walk-in");

  const [items, setItems] = useState([]);

  const activePriceTypes = useMemo(
    () => ALL_PRICE_TYPES.filter((pt) => priceTypeConfig[pt.configKey]),
    [priceTypeConfig],
  );

  // Auto-fallback if the currently selected priceType is disabled in the location config
  useEffect(() => {
    if (activePriceTypes.length > 0) {
      const currentIsStillValid = activePriceTypes.some(
        (pt) => pt.field === priceType,
      );
      if (!currentIsStillValid) setPriceType(activePriceTypes[0].field);
    }
  }, [activePriceTypes, priceType]);

  useEffect(() => {
    const fetchBase = async () => {
      setIsFetchingBase(true);
      try {
        const [custRes, locRes] = await Promise.all([
          axios.get(`${domain}/api/Customers`),
          axios.get(`${domain}/api/Locations`),
        ]);
        setCustomers(custRes.data || []);
        setLocations(locRes.data || []);
      } catch {
        toast.error("Failed to load customers and locations.");
      } finally {
        setIsFetchingBase(false);
      }
    };
    fetchBase();
  }, []);

  const fetchPriceConfig = useCallback(
    async (locationId) => {
      if (!locationId) return;
      setIsConfigLoading(true);
      try {
        const res = await axios.get(
          `${domain}/api/PersonalizedPricing?locationId=${locationId}`,
        );
        const configs = res.data;
        let matched = null;
        if (Array.isArray(configs) && configs.length > 0) {
          matched =
            configs.find((c) => c.locationId === locationId) ||
            configs.find((c) => !c.locationId) ||
            configs[0];
        }
        if (matched) {
          dispatch(
            SET_PriceTypeConfig({
              showVatInc: matched.showVatInc,
              showVatEx: matched.showVatEx,
              showReseller: matched.showReseller,
              showZeroRated: matched.showZeroRated,
              configName: matched.configName,
            }),
          );
        } else {
          const globalRes = await axios.get(
            `${domain}/api/PersonalizedPricing`,
          );
          const globalConfigs = globalRes.data;
          const globalMatch = Array.isArray(globalConfigs)
            ? globalConfigs.find((c) => !c.locationId) || globalConfigs[0]
            : null;
          if (globalMatch) {
            dispatch(
              SET_PriceTypeConfig({
                showVatInc: globalMatch.showVatInc,
                showVatEx: globalMatch.showVatEx,
                showReseller: globalMatch.showReseller,
                showZeroRated: globalMatch.showZeroRated,
                configName: globalMatch.configName,
              }),
            );
          }
        }
      } catch {
        // Silently fail, Redux keeps existing config
      } finally {
        setIsConfigLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!selectedLocation?.id) {
      setPricelists([]);
      setItems([]);
      setReturnedSerials({});
      return;
    }
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const res = await axios.get(
          `${domain}/api/Products/pos-pricelist-by-location?locationId=${selectedLocation.id}`,
        );
        setPricelists(res.data || []);
      } catch {
        toast.error("Failed to load products for this location.");
        setPricelists([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
    fetchPriceConfig(selectedLocation.id);
  }, [selectedLocation?.id, fetchPriceConfig]);

  // ─── HANDLERS ─────────────────────────

  const handleCustomerSelect = (customer) => {
    const customerPriceType = customer.priceType || customer.PriceType;

    if (customerPriceType && customerPriceType !== priceType) {
      const isAvailableInDropdown = activePriceTypes.some(
        (pt) => pt.field === customerPriceType,
      );

      if (isAvailableInDropdown) {
        if (items.length > 0) {
          if (
            !window.confirm(
              `Selecting this customer automatically changes the Price Type to '${customerPriceType}', which clears your currently added items. Continue?`,
            )
          ) {
            return;
          }
          setItems([]);
          setReturnedSerials({});
        }
        setPriceType(customerPriceType);
      }
    }

    setSelectedCustomer(customer);
    setIsCustomerModalOpen(false);
  };

  const handleLocationChange = (e) => {
    const locId = parseInt(e.target.value);
    const loc = locations.find((l) => l.id === locId);
    if (items.length > 0) {
      if (
        !window.confirm(
          "Changing location will clear your added items. Continue?",
        )
      )
        return;
      setItems([]);
      setReturnedSerials({});
    }
    setSelectedLocation(loc || null);
  };

  const handlePriceTypeChange = (e) => {
    const selected = e.target.value;
    if (items.length > 0) {
      if (
        !window.confirm(
          "Changing price type will clear your current selection. Continue?",
        )
      )
        return;
      setItems([]);
      setReturnedSerials({});
    }
    setPriceType(selected);
  };

  const handleAddProduct = (product) => {
    if (!priceType) return toast.error("Please select a Price Type first.");
    if (items.find((i) => i.productId === product.productId)) return;

    setItems((prev) => [
      ...prev,
      {
        productId: product.productId,
        productName: product.productName,
        salesUomName: product.uomName || "Unit",
        qty: 1,
        unitPrice: product[priceType] || 0,
        hasSerial: product.hasSerial,
      },
    ]);
    toast.success(`Added ${product.productName}`, {
      autoClose: 1500,
      hideProgressBar: true,
    });
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };

      if (field === "qty") {
        const newSerials = { ...returnedSerials };
        delete newSerials[idx];
        setReturnedSerials(newSerials);
      }
      return updated;
    });
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setReturnedSerials((prev) => {
      const updated = { ...prev };
      delete updated[idx];
      return updated;
    });
  };

  const getExcludedSerials = (rowIndex) => {
    let usedIds = [];
    Object.keys(returnedSerials).forEach((key) => {
      if (parseInt(key) !== rowIndex && Array.isArray(returnedSerials[key]))
        usedIds = [...usedIds, ...returnedSerials[key]];
    });
    return usedIds;
  };

  const totalRefund = items.reduce(
    (sum, item) =>
      sum + (parseFloat(item.qty) || 0) * (parseFloat(item.unitPrice) || 0),
    0,
  );

  const canProceed = () => {
    if (!selectedCustomer)
      return (toast.error("Please select a customer."), false);
    if (!selectedLocation)
      return (toast.error("Please select a location."), false);
    if (!priceType) return (toast.error("Please select a price type."), false);
    if (items.length === 0)
      return (toast.error("Please add at least one item to return."), false);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const reqQty = parseFloat(item.qty) || 0;

      if (reqQty <= 0) {
        toast.error(`Enter a valid quantity for ${item.productName}.`);
        return false;
      }
      if (item.hasSerial) {
        const selectedCount = returnedSerials[i]?.length || 0;
        if (selectedCount !== reqQty) {
          toast.error(
            `Serial mismatch for ${item.productName}: Required ${reqQty}, Selected ${selectedCount}.`,
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setIsLoadingSubmit(true);
    try {
      const payload = {
        UserID: currentUserId,
        CustomerId: selectedCustomer.id || null,
        CustomerName: selectedCustomer.customerName,
        LocationId: selectedLocation?.id || null,
        LocationName: selectedLocation?.locationName || null,
        Reason: reason,
        LogisticsType: logisticsType, // Passed to Backend
        Items: items.map((i, idx) => ({
          ProductId: i.productId,
          ProductName: i.productName,
          QuantityReturned: parseFloat(i.qty) || 0,
          UnitPrice: parseFloat(i.unitPrice) || 0,
          SerialNumbers: returnedSerials[idx] || [],
        })),
      };

      await axios.post(`${domain}/api/TradeReturns`, payload);
      toast.success("Trade Return created successfully.");
      onSuccess();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to create Trade Return.",
      );
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="relative flex h-full max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="shrink-0 border-b border-slate-100 bg-white px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-600 rounded-xl text-white shadow-md shadow-emerald-200">
                <RotateCcw size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 leading-none">
                  New Trade Return
                </h2>
                <p className="text-[11px] font-bold tracking-wide text-slate-400 mt-1 uppercase">
                  Manual return — no Delivery Order required
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <StepIndicator step={step} />
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/50">
          {step === 1 && (
            <div className="p-6 space-y-5">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <User size={13} className="text-emerald-500" /> Information
                  Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                  <div className="md:col-span-1">
                    <label className={labelCls}>
                      Customer <span className="text-red-500">*</span>
                    </label>
                    <button
                      onClick={() => setIsCustomerModalOpen(true)}
                      disabled={isFetchingBase}
                      className={`w-full flex items-center justify-between p-3 border rounded-xl transition-all text-left ${selectedCustomer ? "border-emerald-300 bg-emerald-50/30" : "border-slate-300 bg-white hover:border-emerald-400"} ${isFetchingBase ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {selectedCustomer ? (
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-1.5 bg-emerald-100 text-emerald-600 rounded-md shrink-0">
                            <User size={14} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800 truncate">
                              {selectedCustomer.customerName}
                            </p>
                            {selectedCustomer.address && (
                              <p className="text-[10px] text-slate-500 truncate mt-0.5">
                                {selectedCustomer.address}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Search size={16} />
                          <span className="text-sm font-semibold">
                            Click to select...
                          </span>
                        </div>
                      )}
                    </button>
                  </div>

                  <div className="md:col-span-1">
                    <label className={labelCls}>
                      Location <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <select
                        value={selectedLocation?.id || ""}
                        onChange={handleLocationChange}
                        className={`${inputCls} pl-9 appearance-none cursor-pointer py-3.5`}
                        disabled={isFetchingBase}
                      >
                        <option value="">
                          {isFetchingBase
                            ? "Loading..."
                            : "— Select Location —"}
                        </option>
                        {locations.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.locationName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={14}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-1">
                    <label className={labelCls}>
                      Price Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Settings2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <select
                        value={priceType}
                        onChange={handlePriceTypeChange}
                        className={`${inputCls} pl-9 appearance-none cursor-pointer py-3.5`}
                        disabled={
                          activePriceTypes.length === 0 || isConfigLoading
                        }
                      >
                        <option value="" disabled>
                          {isConfigLoading
                            ? "Loading config..."
                            : "— Select Price Tier —"}
                        </option>
                        {activePriceTypes.map((pt) => (
                          <option key={pt.field} value={pt.field}>
                            {pt.fullLabel}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={14}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-1">
                    <label className={labelCls}>
                      Logistics Action <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Truck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <select
                        value={logisticsType}
                        onChange={(e) => setLogisticsType(e.target.value)}
                        className={`${inputCls} pl-9 appearance-none cursor-pointer py-3.5`}
                      >
                        <option value="Walk-in">
                          Customer Drop-off (Walk-in)
                        </option>
                        <option value="Pull Out">
                          Pull Out (Logistics Pickup)
                        </option>
                      </select>
                      <ChevronDown
                        size={14}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-4">
                    <label className={labelCls}>Reason for Return</label>
                    <textarea
                      rows={1}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is the customer returning these items? (Optional)"
                      className={inputCls + " resize-none"}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <Package size={13} className="text-emerald-500" /> Returned
                    Products
                  </h3>
                  {selectedLocation && priceType && !isLoadingProducts && (
                    <button
                      onClick={() => setIsProductModalOpen(true)}
                      className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-emerald-700 active:scale-95"
                    >
                      <Plus size={16} /> Browse Products
                    </button>
                  )}
                </div>

                {!selectedLocation || !priceType ? (
                  <div className="text-center py-8 bg-amber-50 m-5 rounded-xl border border-amber-200 text-amber-700">
                    <MapPin size={22} className="mx-auto mb-2 opacity-60" />
                    <p className="text-sm font-bold">
                      Please select Location and Price Type first.
                    </p>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <Package
                      size={28}
                      className="mx-auto mb-3 text-slate-200"
                    />
                    <p className="text-sm font-semibold">
                      No products added yet.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase text-slate-500 font-black tracking-wider">
                        <tr>
                          <th className="px-5 py-3">Product</th>
                          <th className="px-5 py-3 text-right w-28">
                            Unit Price
                          </th>
                          <th className="px-5 py-3 text-center w-24">Qty</th>
                          <th className="px-5 py-3 text-center w-32">
                            Serials
                          </th>
                          <th className="px-5 py-3 text-right w-32">
                            Line Refund
                          </th>
                          <th className="px-5 py-3 text-center w-20">Remove</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, idx) => {
                          const reqQty = parseFloat(item.qty) || 0;
                          const selectedCount =
                            returnedSerials[idx]?.length || 0;
                          const isReady =
                            selectedCount > 0 && selectedCount === reqQty;

                          return (
                            <tr
                              key={item.productId}
                              className="hover:bg-slate-50/50"
                            >
                              <td className="px-5 py-3">
                                <p className="font-bold text-slate-800 text-sm">
                                  {item.productName}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">
                                  {item.salesUomName}
                                </p>
                              </td>
                              <td className="px-5 py-3">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.unitPrice}
                                  onChange={(e) =>
                                    updateItem(idx, "unitPrice", e.target.value)
                                  }
                                  className="w-full text-right py-1.5 px-2 border border-slate-300 rounded-lg text-sm font-bold outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                                />
                              </td>
                              <td className="px-5 py-3">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.qty}
                                  onChange={(e) =>
                                    updateItem(idx, "qty", e.target.value)
                                  }
                                  className="w-full text-center py-1.5 border border-slate-300 rounded-lg text-sm font-black outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                                />
                              </td>
                              <td className="px-5 py-3 text-center">
                                {item.hasSerial ? (
                                  <button
                                    onClick={() => {
                                      setCurrentSerialProduct({
                                        ...item,
                                        rowIndex: idx,
                                        locationId: selectedLocation?.id,
                                      });
                                      setIsSerialModalOpen(true);
                                    }}
                                    className={`inline-flex items-center justify-center w-full gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] font-black transition-all border ${
                                      isReady
                                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700"
                                    }`}
                                  >
                                    <ScanBarcode size={12} />
                                    {selectedCount > 0
                                      ? `${selectedCount}/${reqQty}`
                                      : "Select"}
                                  </button>
                                ) : (
                                  <span className="text-slate-300 text-xs font-semibold">
                                    —
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3 text-right font-black text-red-500">
                                {formatCurrency(
                                  (parseFloat(item.qty) || 0) *
                                    (parseFloat(item.unitPrice) || 0),
                                )}
                              </td>
                              <td className="px-5 py-3 text-center">
                                <button
                                  onClick={() => removeItem(idx)}
                                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="bg-slate-50 px-5 py-4 border-t border-slate-100 flex justify-end items-center gap-4">
                      <span className="text-[11px] font-black uppercase text-slate-400 tracking-widest">
                        Total Base Refund:
                      </span>
                      <span className="text-xl font-black text-slate-800">
                        {formatCurrency(totalRefund)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6 space-y-5">
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-end">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Return Customer
                    </p>
                    <p className="font-black text-slate-800 text-xl">
                      {selectedCustomer?.customerName}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs font-semibold text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={12} className="text-emerald-500" />{" "}
                        {selectedLocation?.locationName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Settings2 size={12} className="text-emerald-500" />{" "}
                        {
                          ALL_PRICE_TYPES.find((p) => p.field === priceType)
                            ?.label
                        }
                      </span>
                      <span className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                        <Truck size={12} className="text-emerald-500" />{" "}
                        {logisticsType}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Total Est. Refund
                    </p>
                    <p className="font-black text-red-600 text-3xl leading-none">
                      {formatCurrency(totalRefund)}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-[10px] font-black uppercase text-slate-400 mb-3 tracking-widest">
                    Items to Return ({items.length})
                  </h4>
                  <div className="space-y-3">
                    {items.map((item, idx) => (
                      <div
                        key={item.productId}
                        className="flex justify-between items-center p-3.5 bg-white border border-slate-200 rounded-xl shadow-sm"
                      >
                        <div>
                          <span className="font-bold text-slate-800 block text-sm">
                            {item.productName}
                          </span>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded inline-block">
                              {item.qty} {item.salesUomName} @{" "}
                              {formatCurrency(item.unitPrice)}
                            </span>
                            {item.hasSerial && returnedSerials[idx] && (
                              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded inline-block">
                                {returnedSerials[idx].length} Serials Attached
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="font-black text-slate-700">
                          {formatCurrency(item.qty * item.unitPrice)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {reason && (
                    <div className="mt-5 pt-4 border-t border-slate-200 text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
                      <span className="font-bold text-amber-800 mr-2">
                        Reason:
                      </span>
                      {reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-white px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-slate-500 font-bold">
            {step === 1 && items.length > 0 && (
              <span className="text-emerald-600">
                {items.length} Product(s)
              </span>
            )}
          </div>
          <div className="flex gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors"
              >
                Back to Edit
              </button>
            )}
            {step < 2 ? (
              <button
                onClick={() => {
                  if (canProceed()) setStep(2);
                }}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 active:scale-95 disabled:opacity-50"
                disabled={isLoadingProducts || isFetchingBase}
              >
                Review Items <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isLoadingSubmit}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-black text-white bg-emerald-600 rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 active:scale-95 disabled:opacity-50"
              >
                {isLoadingSubmit ? (
                  <Loader2 className="animate-spin w-4 h-4" />
                ) : (
                  <RotateCcw size={15} />
                )}
                {isLoadingSubmit
                  ? "Creating Return..."
                  : "Confirm & Create Return"}
              </button>
            )}
          </div>
        </div>
      </div>

      <CustomerSelectionModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        customers={customers}
        onSelect={handleCustomerSelect}
      />

      <ProductSelectionModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        products={pricelists}
        items={items}
        priceType={priceType}
        onAdd={handleAddProduct}
      />

      {isSerialModalOpen && currentSerialProduct && (
        <SelectedSerialModal
          product={{
            ...currentSerialProduct,
            quantity: currentSerialProduct.qty,
            conversionRate: 1,
          }}
          excludedSerialIds={getExcludedSerials(currentSerialProduct.rowIndex)}
          onClose={() => {
            setIsSerialModalOpen(false);
            setCurrentSerialProduct(null);
          }}
          onSave={(productId, serials) => {
            setReturnedSerials((prev) => ({
              ...prev,
              [currentSerialProduct.rowIndex]: serials,
            }));
            setIsSerialModalOpen(false);
            setCurrentSerialProduct(null);
            toast.success("Return serials updated.");
          }}
        />
      )}
    </div>
  );
}
